import { Button } from "@/components/ui/button";
import {
  APP_UPDATE_PROMPT_ADMIN_PT,
  APP_UPDATE_PROMPT_TRANSLATIONS as T,
} from "@/domains/shared/appUpdatePrompt/appUpdatePromptConstants";
import { isAppUpdateDirty } from "@/domains/shared/appUpdatePrompt/isAppUpdateDirty";
import { useAppUpdatePromptStore } from "@/domains/shared/appUpdatePrompt/appUpdatePromptStore";
import { getTranslation, useLocale } from "@mivo/i18n";
import { useLocation } from "react-router";

function useIsAdminPath(): boolean {
  const { pathname } = useLocation();
  return pathname.startsWith("/admin");
}

export function AppUpdateSoftBanner() {
  useLocale();
  const isAdmin = useIsAdminPath();
  const softBannerVisible = useAppUpdatePromptStore((s) => s.softBannerVisible);
  const hardActive = useAppUpdatePromptStore((s) => s.hardActive);
  const dismissSoftLater = useAppUpdatePromptStore((s) => s.dismissSoftLater);
  const requestReloadNow = useAppUpdatePromptStore((s) => s.requestReloadNow);

  if (!softBannerVisible || hardActive) {
    return null;
  }

  const message = isAdmin ? APP_UPDATE_PROMPT_ADMIN_PT.softMessage : getTranslation(T.softMessage);
  const reloadLabel = isAdmin
    ? APP_UPDATE_PROMPT_ADMIN_PT.softReloadNow
    : getTranslation(T.softReloadNow);
  const laterLabel = isAdmin ? APP_UPDATE_PROMPT_ADMIN_PT.softLater : getTranslation(T.softLater);

  return (
    <div
      data-app-update-prompt
      data-testid="app-update-soft-banner"
      role="status"
      className="fixed inset-x-0 bottom-0 z-[45] border-t border-border bg-background/95 px-3 py-3 shadow-lg backdrop-blur-sm sm:bottom-4 sm:inset-x-auto sm:right-4 sm:left-auto sm:w-[min(100%-2rem,24rem)] sm:rounded-xl sm:border"
    >
      <div className="mx-auto flex w-full max-w-lg flex-col gap-3 sm:max-w-none">
        <p className="text-sm text-foreground">{message}</p>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto min-h-11"
            data-testid="app-update-soft-later"
            onClick={() => {
              dismissSoftLater();
            }}
          >
            {laterLabel}
          </Button>
          <Button
            type="button"
            className="w-full sm:w-auto min-h-11"
            data-testid="app-update-soft-reload"
            onClick={() => {
              // E-07: Reload now while dirty still enters hard path with warning.
              requestReloadNow({ unsavedLoss: isAppUpdateDirty() });
            }}
          >
            {reloadLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
